import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import LocationAutocomplete from '../components/LocationAutocomplete';
import { 
  ArrowLeft, 
  ArrowRight, 
  CalendarIcon, 
  MapPin, 
  Image as ImageIcon,
  Plus,
  Trash2,
  Gavel,
  Upload,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const categories = [
  { id: 'gaming', name: 'Gaming' },
  { id: 'beauty', name: 'Beauty & Fashion' },
  { id: 'music', name: 'Music & Concerts' },
  { id: 'comedy', name: 'Comedy' },
  { id: 'fitness', name: 'Fitness & Sports' },
  { id: 'food', name: 'Food & Cooking' },
  { id: 'tech', name: 'Tech & Business' },
  { id: 'lifestyle', name: 'Lifestyle' }
];

export default function CreateEvent() {
  const navigate = useNavigate();
  const { isAuthenticated, isInfluencer, loading: authLoading } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    category: '',
    event_type: 'in-person',
    location: '',
    location_coords: null,
    location_mode: 'specific', // 'specific', 'after_sales', 'tbd'
    event_date: null,
    date_mode: 'specific', // 'specific', 'flexible'
    date_flexible: '', // e.g., "Q4 2025", "Summer 2026"
    image_url: '',
    is_private: false
  });
  const [ticketTiers, setTicketTiers] = useState([
    { name: 'General Admission', description: '', price: '', quantity: '', sale_mode: 'fixed', min_bid: '' }
  ]);
  const [validationAttempted, setValidationAttempted] = useState({ step1: false, step2: false });

  // Redirect if not authenticated or not influencer
  if (!authLoading && !isAuthenticated) {
    navigate('/login');
    return null;
  }
  if (!authLoading && isAuthenticated && !isInfluencer) {
    navigate('/');
    return null;
  }

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, WebP, or GIF.');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }
    
    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setEventData({ ...eventData, image_url: '' });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const addTicketTier = () => {
    setTicketTiers([...ticketTiers, { name: '', description: '', price: '', quantity: '', sale_mode: 'fixed', min_bid: '' }]);
  };

  const removeTicketTier = (index) => {
    if (ticketTiers.length > 1) {
      setTicketTiers(ticketTiers.filter((_, i) => i !== index));
    }
  };

  const updateTicketTier = (index, field, value) => {
    const updated = [...ticketTiers];
    updated[index][field] = value;
    setTicketTiers(updated);
  };

  // Step 1 validation: Event Details
  const validateStep1 = () => {
    const errors = [];
    
    if (!eventData.title.trim()) {
      errors.push('Event title is required');
    }
    if (!eventData.description.trim()) {
      errors.push('Event description is required');
    }
    if (!eventData.category) {
      errors.push('Please select a category');
    }
    if (eventData.location_mode === 'specific' && !eventData.location.trim()) {
      errors.push('Location is required');
    }
    if (eventData.date_mode === 'specific' && !eventData.event_date) {
      errors.push('Event date is required');
    }
    if (eventData.date_mode === 'flexible' && !eventData.date_flexible.trim()) {
      errors.push('Please specify a flexible date range (e.g., "Q4 2025")');
    }
    
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err));
      return false;
    }
    return true;
  };

  // Step 2 validation: Ticket Tiers
  const validateStep2 = () => {
    const errors = [];
    let hasValidTier = false;
    
    ticketTiers.forEach((tier, index) => {
      const tierNum = index + 1;
      
      if (!tier.name.trim()) {
        errors.push(`Tier ${tierNum}: Name is required`);
      }
      if (!tier.price || parseFloat(tier.price) <= 0) {
        errors.push(`Tier ${tierNum}: Price must be greater than 0`);
      }
      if (!tier.quantity || parseInt(tier.quantity) <= 0) {
        errors.push(`Tier ${tierNum}: Quantity must be greater than 0`);
      }
      if (tier.sale_mode === 'auction' && tier.min_bid && parseFloat(tier.min_bid) <= 0) {
        errors.push(`Tier ${tierNum}: Minimum bid must be greater than 0`);
      }
      
      // Check if this tier is valid
      if (tier.name.trim() && tier.price && parseFloat(tier.price) > 0 && tier.quantity && parseInt(tier.quantity) > 0) {
        hasValidTier = true;
      }
    });
    
    if (!hasValidTier) {
      errors.push('At least one complete ticket tier is required');
    }
    
    if (errors.length > 0) {
      // Show first 3 errors max to avoid overwhelming the user
      errors.slice(0, 3).forEach(err => toast.error(err));
      if (errors.length > 3) {
        toast.error(`And ${errors.length - 3} more issue(s)...`);
      }
      return false;
    }
    return true;
  };

  const goToStep2 = () => {
    setValidationAttempted({ ...validationAttempted, step1: true });
    if (validateStep1()) {
      setStep(2);
    }
  };

  const goToStep3 = () => {
    setValidationAttempted({ ...validationAttempted, step2: true });
    if (validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    // Validate
    if (!eventData.title || !eventData.description || !eventData.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    const validTiers = ticketTiers.filter(t => t.name && t.price && t.quantity);
    if (validTiers.length === 0) {
      toast.error('Please add at least one ticket tier');
      return;
    }

    setLoading(true);
    try {
      // Build location string based on mode
      let finalLocation = eventData.location;
      if (eventData.location_mode === 'after_sales') {
        finalLocation = 'Location to be decided based on ticket sales';
      } else if (eventData.location_mode === 'tbd') {
        finalLocation = 'Location TBD';
      }

      // Build date info
      let eventDate = eventData.event_date ? eventData.event_date.toISOString() : null;
      let dateDisplay = eventData.date_flexible || null;
      if (eventData.date_mode === 'flexible' && eventData.date_flexible) {
        eventDate = null; // No specific date
        dateDisplay = eventData.date_flexible;
      }

      // Create event (without image first)
      const eventPayload = {
        ...eventData,
        location: finalLocation,
        location_coords: eventData.location_mode === 'specific' ? eventData.location_coords : null,
        event_date: eventDate,
        date_display: dateDisplay,
        image_url: eventData.image_url || '' // Will be updated if file upload
      };
      const eventRes = await axios.post(`${API_URL}/events`, eventPayload);
      const eventId = eventRes.data.id;

      // Upload image if file was selected
      if (imageFile) {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', imageFile);
        try {
          await axios.post(`${API_URL}/events/${eventId}/upload-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (imgError) {
          console.error('Image upload failed:', imgError);
          toast.warning('Event created but image upload failed. You can add it later.');
        }
        setUploadingImage(false);
      }

      // Add ticket tiers
      for (const tier of validTiers) {
        await axios.post(`${API_URL}/events/${eventId}/ticket-tiers`, {
          name: tier.name,
          description: tier.description,
          base_price: parseFloat(tier.price),
          quantity: parseInt(tier.quantity),
          sale_mode: tier.sale_mode,
          min_bid: tier.sale_mode === 'auction' && tier.min_bid ? parseFloat(tier.min_bid) : null
        });
      }

      // Publish event
      await axios.put(`${API_URL}/events/${eventId}/publish`);

      toast.success('Event created and published!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button variant="ghost" className="gap-2 mb-4" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Button>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl">Create New Event</h1>
          <p className="text-muted-foreground mt-2">Set up your event and start selling tickets</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 1 ? 'bg-primary text-white' : 'bg-secondary'}`}>
              1
            </div>
            <span className="hidden sm:inline">Details</span>
          </div>
          <div className="w-12 h-0.5 bg-secondary" />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 2 ? 'bg-primary text-white' : 'bg-secondary'}`}>
              2
            </div>
            <span className="hidden sm:inline">Tickets</span>
          </div>
          <div className="w-12 h-0.5 bg-secondary" />
          <div className={`flex items-center gap-2 ${step >= 3 ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 3 ? 'bg-primary text-white' : 'bg-secondary'}`}>
              3
            </div>
            <span className="hidden sm:inline">Review</span>
          </div>
        </div>

        {/* Step 1: Event Details */}
        {step === 1 && (
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle>Event Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Gaming Night with AlexGamer"
                  value={eventData.title}
                  onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                  className={`mt-1 ${validationAttempted.step1 && !eventData.title.trim() ? 'border-red-500 focus:ring-red-500' : ''}`}
                  data-testid="event-title-input"
                />
                {validationAttempted.step1 && !eventData.title.trim() && (
                  <p className="text-red-500 text-sm mt-1">Event title is required</p>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Describe your event..."
                  value={eventData.description}
                  onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                  rows={4}
                  className={`mt-1 ${validationAttempted.step1 && !eventData.description.trim() ? 'border-red-500 focus:ring-red-500' : ''}`}
                  data-testid="event-description-input"
                />
                {validationAttempted.step1 && !eventData.description.trim() && (
                  <p className="text-red-500 text-sm mt-1">Description is required</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Category *</Label>
                  <Select value={eventData.category} onValueChange={(v) => setEventData({ ...eventData, category: v })}>
                    <SelectTrigger className={`mt-1 ${validationAttempted.step1 && !eventData.category ? 'border-red-500' : ''}`} data-testid="event-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {validationAttempted.step1 && !eventData.category && (
                    <p className="text-red-500 text-sm mt-1">Please select a category</p>
                  )}
                </div>

                <div>
                  <Label>Event Type</Label>
                  <Select value={eventData.event_type} onValueChange={(v) => setEventData({ ...eventData, event_type: v })}>
                    <SelectTrigger className="mt-1" data-testid="event-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-person">In-Person</SelectItem>
                      <SelectItem value="virtual">Virtual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Date Section */}
                <div className="space-y-3">
                  <Label>Event Date</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={eventData.date_mode === 'specific' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEventData({ ...eventData, date_mode: 'specific' })}
                        className="flex-1"
                      >
                        Specific Date
                      </Button>
                      <Button
                        type="button"
                        variant={eventData.date_mode === 'flexible' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEventData({ ...eventData, date_mode: 'flexible' })}
                        className="flex-1"
                      >
                        Flexible
                      </Button>
                    </div>
                    
                    {eventData.date_mode === 'specific' ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !eventData.event_date && "text-muted-foreground"
                            )}
                            data-testid="event-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {eventData.event_date ? format(eventData.event_date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={eventData.event_date}
                            onSelect={(date) => setEventData({ ...eventData, event_date: date })}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            defaultMonth={new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <div>
                        <Select 
                          value={eventData.date_flexible} 
                          onValueChange={(v) => setEventData({ ...eventData, date_flexible: v })}
                        >
                          <SelectTrigger data-testid="event-date-flexible">
                            <SelectValue placeholder="Select timeframe" />
                          </SelectTrigger>
                          <SelectContent>
                            {(() => {
                              const now = new Date();
                              const currentYear = now.getFullYear();
                              const currentMonth = now.getMonth();
                              const currentQuarter = Math.floor(currentMonth / 3) + 1;
                              const nextYear = currentYear + 1;
                              
                              const options = [];
                              
                              // Add remaining quarters of current year
                              for (let q = currentQuarter; q <= 4; q++) {
                                const months = q === 1 ? 'Jan-Mar' : q === 2 ? 'Apr-Jun' : q === 3 ? 'Jul-Sep' : 'Oct-Dec';
                                options.push({ value: `Q${q} ${currentYear}`, label: `Q${q} ${currentYear} (${months})` });
                              }
                              
                              // Add all quarters of next year
                              for (let q = 1; q <= 4; q++) {
                                const months = q === 1 ? 'Jan-Mar' : q === 2 ? 'Apr-Jun' : q === 3 ? 'Jul-Sep' : 'Oct-Dec';
                                options.push({ value: `Q${q} ${nextYear}`, label: `Q${q} ${nextYear} (${months})` });
                              }
                              
                              // Add seasons
                              const seasons = ['Spring', 'Summer', 'Fall', 'Winter'];
                              const currentSeasonIndex = Math.floor((currentMonth + 1) / 3) % 4;
                              
                              for (let i = currentSeasonIndex; i < 4; i++) {
                                options.push({ value: `${seasons[i]} ${currentYear}`, label: `${seasons[i]} ${currentYear}` });
                              }
                              for (let i = 0; i < 4; i++) {
                                options.push({ value: `${seasons[i]} ${nextYear}`, label: `${seasons[i]} ${nextYear}` });
                              }
                              
                              // Add year options
                              options.push({ value: `${currentYear}`, label: `Sometime in ${currentYear}` });
                              options.push({ value: `${nextYear}`, label: `Sometime in ${nextYear}` });
                              options.push({ value: 'TBD', label: 'To Be Announced' });
                              
                              return options.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ));
                            })()}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Exact date will be announced later
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Location Section */}
                <div className="space-y-3">
                  <Label>Location</Label>
                  <div className="space-y-2">
                    <Select 
                      value={eventData.location_mode} 
                      onValueChange={(v) => setEventData({ ...eventData, location_mode: v, location: '' })}
                    >
                      <SelectTrigger data-testid="location-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="specific">Specific Location</SelectItem>
                        <SelectItem value="after_sales">Decide After Ticket Sales</SelectItem>
                        <SelectItem value="tbd">To Be Decided Later</SelectItem>
                      </SelectContent>
                    </Select>

                    {eventData.location_mode === 'specific' && (
                      <LocationAutocomplete
                        value={eventData.location}
                        onChange={(location, details) => {
                          setEventData({ 
                            ...eventData, 
                            location,
                            location_coords: details ? { lat: details.lat, lng: details.lng } : null
                          });
                        }}
                        placeholder="Search for a city or venue..."
                      />
                    )}

                    {eventData.location_mode === 'after_sales' && (
                      <p className="text-sm text-muted-foreground bg-secondary/30 rounded-lg p-3">
                        📍 Location will be chosen based on where most ticket buyers are located. 
                        This helps you pick the best venue for your audience!
                      </p>
                    )}

                    {eventData.location_mode === 'tbd' && (
                      <p className="text-sm text-muted-foreground bg-secondary/30 rounded-lg p-3">
                        📍 Location will be announced at a later date. 
                        Ticket holders will be notified when confirmed.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Label>Event Image</Label>
                <div className="mt-1">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleImageSelect}
                    className="hidden"
                    data-testid="event-image-file-input"
                  />
                  
                  {imagePreview || eventData.image_url ? (
                    <div className="relative rounded-lg overflow-hidden bg-secondary/30">
                      <img 
                        src={imagePreview || eventData.image_url} 
                        alt="Event preview" 
                        className="w-full h-48 object-cover"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-2 right-2"
                        onClick={removeImage}
                        data-testid="remove-image-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                      data-testid="upload-image-area"
                    >
                      <Upload className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-sm font-medium">Click to upload event image</p>
                      <p className="text-xs text-muted-foreground mt-1">JPEG, PNG, WebP or GIF (max 5MB)</p>
                    </div>
                  )}
                  
                  {/* Fallback URL input */}
                  <div className="mt-3">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                      <div className="flex-1 h-px bg-white/10" />
                      <span>or use URL</span>
                      <div className="flex-1 h-px bg-white/10" />
                    </div>
                    <div className="relative">
                      <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="https://..."
                        value={eventData.image_url}
                        onChange={(e) => {
                          setEventData({ ...eventData, image_url: e.target.value });
                          setImageFile(null);
                          setImagePreview(null);
                        }}
                        className="pl-10"
                        disabled={!!imageFile}
                        data-testid="event-image-url-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Private Event</Label>
                  <p className="text-sm text-muted-foreground">Only people with a link can see this event</p>
                </div>
                <Switch
                  checked={eventData.is_private}
                  onCheckedChange={(checked) => setEventData({ ...eventData, is_private: checked })}
                  data-testid="event-private-switch"
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={goToStep2} className="gap-2" data-testid="next-step-btn">
                  Next: Tickets <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Ticket Tiers */}
        {step === 2 && (
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle>Ticket Tiers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {ticketTiers.map((tier, index) => (
                <div key={index} className="p-4 border border-white/10 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold">Tier {index + 1}</h4>
                    {ticketTiers.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeTicketTier(index)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Tier Name *</Label>
                      <Input
                        placeholder="e.g., VIP Access"
                        value={tier.name}
                        onChange={(e) => updateTicketTier(index, 'name', e.target.value)}
                        className={`mt-1 ${validationAttempted.step2 && !tier.name.trim() ? 'border-red-500' : ''}`}
                        data-testid={`tier-${index}-name`}
                      />
                      {validationAttempted.step2 && !tier.name.trim() && (
                        <p className="text-red-500 text-xs mt-1">Required</p>
                      )}
                    </div>
                    <div>
                      <Label>Sale Mode</Label>
                      <Select value={tier.sale_mode} onValueChange={(v) => updateTicketTier(index, 'sale_mode', v)}>
                        <SelectTrigger className="mt-1" data-testid={`tier-${index}-mode`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed Price</SelectItem>
                          <SelectItem value="auction">Auction / Bidding</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Input
                      placeholder="What's included in this tier?"
                      value={tier.description}
                      onChange={(e) => updateTicketTier(index, 'description', e.target.value)}
                      className="mt-1"
                      data-testid={`tier-${index}-description`}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Price ($) *</Label>
                      <Input
                        type="number"
                        placeholder="50"
                        value={tier.price}
                        onChange={(e) => updateTicketTier(index, 'price', e.target.value)}
                        className={`mt-1 ${validationAttempted.step2 && (!tier.price || parseFloat(tier.price) <= 0) ? 'border-red-500' : ''}`}
                        data-testid={`tier-${index}-price`}
                      />
                      {validationAttempted.step2 && (!tier.price || parseFloat(tier.price) <= 0) && (
                        <p className="text-red-500 text-xs mt-1">Price required</p>
                      )}
                    </div>
                    <div>
                      <Label>Quantity *</Label>
                      <Input
                        type="number"
                        placeholder="100"
                        value={tier.quantity}
                        onChange={(e) => updateTicketTier(index, 'quantity', e.target.value)}
                        className={`mt-1 ${validationAttempted.step2 && (!tier.quantity || parseInt(tier.quantity) <= 0) ? 'border-red-500' : ''}`}
                        data-testid={`tier-${index}-quantity`}
                      />
                      {validationAttempted.step2 && (!tier.quantity || parseInt(tier.quantity) <= 0) && (
                        <p className="text-red-500 text-xs mt-1">Quantity required</p>
                      )}
                    </div>
                    {tier.sale_mode === 'auction' && (
                      <div>
                        <Label>Min Bid ($)</Label>
                        <Input
                          type="number"
                          placeholder="Same as price"
                          value={tier.min_bid}
                          onChange={(e) => updateTicketTier(index, 'min_bid', e.target.value)}
                          className="mt-1"
                          data-testid={`tier-${index}-minbid`}
                        />
                      </div>
                    )}
                  </div>

                  {tier.sale_mode === 'auction' && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/50 p-3 rounded-lg">
                      <Gavel className="w-4 h-4 text-primary" />
                      <span>Fans will bid for this tier. Highest bidders win tickets.</span>
                    </div>
                  )}
                </div>
              ))}

              <Button variant="outline" onClick={addTicketTier} className="w-full gap-2" data-testid="add-tier-btn">
                <Plus className="w-4 h-4" />
                Add Another Tier
              </Button>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={goToStep3} className="gap-2" data-testid="next-review-btn">
                  Next: Review <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle>Review Your Event</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Preview */}
              <div className="border border-white/10 rounded-xl overflow-hidden">
                {(imagePreview || eventData.image_url) && (
                  <img src={imagePreview || eventData.image_url} alt={eventData.title} className="w-full h-48 object-cover" />
                )}
                <div className="p-4">
                  <h3 className="font-heading font-bold text-xl">{eventData.title || 'Event Title'}</h3>
                  <p className="text-muted-foreground text-sm mt-1 line-clamp-2">{eventData.description}</p>
                  <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                    <span className="capitalize">{eventData.category || 'Category'}</span>
                    {eventData.event_date && <span>{format(eventData.event_date, 'PPP')}</span>}
                    {eventData.location && <span>{eventData.location}</span>}
                  </div>
                </div>
              </div>

              {/* Ticket Summary */}
              <div>
                <h4 className="font-semibold mb-3">Ticket Tiers</h4>
                <div className="space-y-2">
                  {ticketTiers.filter(t => t.name && t.price).map((tier, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                      <div>
                        <span className="font-medium">{tier.name}</span>
                        {tier.sale_mode === 'auction' && (
                          <span className="ml-2 text-xs text-primary">(Auction)</span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="font-semibold">${tier.price}</span>
                        <span className="text-muted-foreground text-sm ml-2">× {tier.quantity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={handleSubmit} disabled={loading} className="gap-2" data-testid="publish-event-btn">
                  {loading ? 'Publishing...' : 'Publish Event'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
